import { useRef, useState } from "react";
import {
  ApiError,
  type BulkAddItemsReport,
  type ExportedInstanceItems,
  type GeneratedDimension,
  type GeneratedNodeAttribute,
  type InstanceCustomisationView,
  type PatchNodeBody,
  type ResolvedItem,
  type ResolvedSection,
  type ResolvedSegment,
  isAiExtractableAttributeType,
  isSegmentWideItemScope,
} from "@keydials/api-client";
import { RichText, richTextHasContent } from "@keydials/ui";
import { api, getAccessToken } from "../auth/AuthContext";
import { Icon } from "../components/Icon";
import { RichTextEditor } from "../components/RichTextEditor";
import { DocumentItemImport } from "./DocumentItemImport";
import { DEFAULT_ITEM_NOUN, DEFAULT_SEGMENT_NOUN, article, itemNouns, sectionSegmentNoun, type ItemNoun, type SegmentNoun } from "@keydials/dial-workspace";

const token = () => getAccessToken() ?? "";

/** Filesystem-safe slug for a download filename. */
function slug(s: string, fallback: string) {
  const out = s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return out || fallback;
}

type NodeType = "Section" | "Segment" | "Item";
type EditField = "label" | "name" | "description" | "guidance";

/** A small pill tag (Custom / Edited / Hidden / Locked). */
function Tag({ label, tone }: { label: string; tone: "brand" | "amber" | "muted" }) {
  const colour =
    tone === "brand" ? "var(--brand-600, #4338ca)" : tone === "amber" ? "var(--st-amber, #b45309)" : "var(--text-faint)";
  return (
    <span
      style={{
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: colour,
        background: "var(--surface-2)",
        border: "var(--border-w) solid var(--border)",
        borderRadius: 99,
        padding: "2px 8px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

/** A bordered text button used for the per-node actions. */
function Act({ children, onClick, disabled, danger, title }: { children: React.ReactNode; onClick: () => void; disabled?: boolean; danger?: boolean; title?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        font: "inherit",
        fontSize: 12,
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 9px",
        borderRadius: "var(--radius-sm)",
        border: "var(--border-w) solid var(--border)",
        background: "var(--surface)",
        color: disabled ? "var(--text-faint)" : danger ? "var(--st-overdue, #dc2626)" : "var(--text-muted)",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

export function EditStructurePanel({
  instanceId,
  instanceName,
  view,
  onChanged,
  docImportEnabled = false,
  itemAttributes = [],
  statusDimension = null,
  noun = DEFAULT_SEGMENT_NOUN,
  guidanceLabel = "Guidance",
}: {
  instanceId: string;
  instanceName: string;
  view: InstanceCustomisationView;
  onChanged: () => void | Promise<void>;
  /** Whether the AI document → items import is enabled on this server (Features:AiItemImport). */
  docImportEnabled?: boolean;
  /** The definition's declared Item-target attributes (for the import review's editable fields). */
  itemAttributes?: GeneratedNodeAttribute[];
  /** The dial's primary item-status dimension (for the import review's status suggestions). */
  statusDimension?: GeneratedDimension | null;
  /** This dial's word for an action point (KEY-539); omitted means the platform's word. */
  noun?: SegmentNoun;
  /** What this dial calls an item's Guidance (KEY-670, `dialLabel(detail, "guidance.item")`), so the editor's field
   *  heading matches the tab the same prose appears under on the item card. Omitted means "Guidance". */
  guidanceLabel?: string;
}) {
  const policy = view.effectivePolicy;
  const headroom = view.headroom;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ key: string; field: EditField; value: string } | null>(null);
  // Two-step delete: first click arms the confirm, second click removes. Only one node armed at a time.
  const [confirming, setConfirming] = useState<string | null>(null);
  // Items whose detail (description) row is expanded.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  // Items-template export/import.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingImport, setPendingImport] = useState<ExportedInstanceItems | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // AI document → items import (flag-gated modal flow).
  const [docImportOpen, setDocImportOpen] = useState(false);
  const customItemCount = view.sections.reduce(
    (n, s) => n + s.segments.reduce((m, sg) => m + sg.items.filter((i) => i.origin === "Custom").length, 0),
    0,
  );

  const locked = (key: string) => policy.lockedSectionKeys.includes(key);
  const sectionsRemaining = headroom.sectionsRemaining; // null = unlimited
  const canAddSection = policy.allowAddSections && (sectionsRemaining == null || sectionsRemaining > 0);
  const segmentRoom = (s: ResolvedSection) => headroom.maxSegmentsPerSection == null || s.segments.length < headroom.maxSegmentsPerSection;
  const itemRoom = (sg: ResolvedSegment) => headroom.maxItemsPerSegment == null || sg.items.length < headroom.maxItemsPerSegment;

  async function run<T>(fn: () => Promise<T>, fail: string, then?: (result: T) => void) {
    setBusy(true);
    setErr(null);
    try {
      const result = await fn();
      setEditing(null);
      setConfirming(null);
      await onChanged();
      then?.(result);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : fail);
    } finally {
      setBusy(false);
    }
  }

  // After adding a node, drop straight into renaming it: the freshly created node carries a placeholder
  // name, so we open its inline editor (text pre-selected) rather than leaving a stray "New …" behind.
  const addSection = () => run(() => api.addInstanceSection(token(), instanceId, { label: "New section" }), "Could not add the section.", ({ key }) => setEditing({ key, field: "label", value: "New section" }));
  // KEY-539: the seeded name a freshly added node carries is the dial's own word, so somebody adding one to
  // the pep dial gets "New segment" rather than a phrase their dial uses nowhere else.
  // KEY-762: a section that names its own lists seeds with its word instead — "New dentist" in Healthcare.
  const listNoun = (section: ResolvedSection): SegmentNoun => sectionSegmentNoun({ sections: [section] }, section.key) ?? noun;
  const addSegment = (parentKey: string, words: SegmentNoun = noun) => run(() => api.addInstanceSegment(token(), instanceId, { parentKey, label: "New", name: `New ${words.one}` }), `Could not add the ${words.one}.`, ({ key }) => setEditing({ key, field: "name", value: `New ${words.one}` }));
  // KEY-753: and an item's is its section's word — "New appointment" on a family dial's Appointments section.
  const addItem = (parentKey: string, words: ItemNoun = DEFAULT_ITEM_NOUN) => run(() => api.addInstanceItem(token(), instanceId, { parentKey, name: `New ${words.one}` }), `Could not add the ${words.one}.`, ({ key }) => setEditing({ key, field: "name", value: `New ${words.one}` }));
  const remove = (key: string) => run(() => api.deleteInstanceNode(token(), instanceId, key), "Could not remove the node.");
  const move = (key: string, targetParentKey: string) => run(() => api.moveInstanceItem(token(), instanceId, key, { targetParentKey }), "Could not move the item.");
  const setDisabled = (key: string, nodeType: NodeType, disabled: boolean) =>
    run(() => api.patchInstanceNode(token(), instanceId, key, { nodeType, disabled }), disabled ? "Could not hide the node." : "Could not show the node.");

  async function exportItems() {
    setBusy(true);
    setErr(null);
    setNote(null);
    try {
      const data = await api.exportInstanceItems(token(), instanceId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug(instanceName, "dial")}.items.json`;
      a.click();
      URL.revokeObjectURL(url);
      setNote(`Exported ${data.items.length} item${data.items.length === 1 ? "" : "s"} to a file.`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not export the items.");
    } finally {
      setBusy(false);
    }
  }

  async function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setErr(null);
    setNote(null);
    let payload: ExportedInstanceItems;
    try {
      payload = JSON.parse(await file.text()) as ExportedInstanceItems;
    } catch {
      setErr("That doesn't look like a valid items file.");
      return;
    }
    if (!payload || !Array.isArray(payload.items)) {
      setErr("That doesn't look like a valid items file.");
      return;
    }
    // Warn before clobbering items the instance already has (replace resets their statuses).
    if (customItemCount > 0) setPendingImport(payload);
    else void doImport(payload);
  }

  async function doImport(payload: ExportedInstanceItems) {
    setBusy(true);
    setErr(null);
    setNote(null);
    setPendingImport(null);
    try {
      const r = await api.importInstanceItems(token(), instanceId, payload);
      const bits = [`Added ${r.added} item${r.added === 1 ? "" : "s"}`];
      if (r.replaced > 0) bits.push(`replaced ${r.replaced}`);
      if (r.skippedNoSegment > 0) bits.push(`skipped ${r.skippedNoSegment} with no matching ${noun.one}`);
      if (r.skippedCapReached > 0) bits.push(`skipped ${r.skippedCapReached} over the item limit`);
      setNote(`${bits.join(", ")}.`);
      await onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not import the items.");
    } finally {
      setBusy(false);
    }
  }

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // `override` carries the latest text for fields whose input is uncontrolled (name/label — see NameCell);
  // controlled fields (description/guidance) fall back to the editing state.
  function saveField(node: { key: string; origin?: string }, nodeType: NodeType, field: EditField, override?: string) {
    if (!editing) return;
    // Names/labels and description are plain text (trim); guidance is sanitised HTML from the rich
    // editor (don't trim). Names must stay non-empty; description and guidance may be cleared.
    const raw = override ?? editing.value;
    const value = field === "guidance" ? raw : raw.trim();
    if (!value && (field === "label" || field === "name")) {
      setEditing(null);
      return;
    }
    // Reflect the typed text so the briefly-disabled input shows what's saving, not the seed value.
    if (override !== undefined) setEditing({ key: node.key, field, value: raw });
    const body: PatchNodeBody = node.origin === "Custom" ? { [field]: value } : { nodeType, [field]: value };
    void run(() => api.patchInstanceNode(token(), instanceId, node.key, body), "Could not save the change.");
  }

  // A single editable name cell with inline rename.
  function NameCell({ node, nodeType, field, display, canRename }: { node: { key: string; origin?: string }; nodeType: NodeType; field: "label" | "name"; display: string; canRename: boolean }) {
    if (editing?.key === node.key && editing.field === field) {
      return (
        <input
          // Uncontrolled while editing: NameCell/ItemRow/SegmentRow are defined inside EditStructurePanel,
          // so a panel re-render remounts this input (losing focus/caret and re-running the select on each
          // keystroke — the "single character" bug). Staying uncontrolled means typing doesn't re-render
          // the panel at all; we read the value on Enter/blur instead.
          autoFocus
          onFocus={(e) => e.currentTarget.select()}
          defaultValue={editing.value}
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveField(node, nodeType, field, e.currentTarget.value);
            if (e.key === "Escape") setEditing(null);
          }}
          onBlur={(e) => saveField(node, nodeType, field, e.currentTarget.value)}
          style={{ font: "inherit", fontSize: 14, fontWeight: 600, flex: 1, minWidth: 0, padding: "3px 7px", border: "var(--border-w) solid var(--brand-400, #818cf8)", borderRadius: "var(--radius-sm)", background: "var(--surface)", color: "var(--text)" }}
        />
      );
    }
    if (!canRename) {
      return (
        <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={display}>
          {display}
        </span>
      );
    }
    const startRename = () => setEditing({ key: node.key, field, value: display });
    return (
      <span style={{ flex: 1, minWidth: 0, display: "inline-flex", alignItems: "center", gap: 4 }}>
        <button
          type="button"
          onClick={startRename}
          style={{ font: "inherit", fontSize: 14, fontWeight: 600, minWidth: 0, textAlign: "left", background: "none", border: "none", color: "var(--text)", cursor: "text", padding: "3px 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title="Click to rename"
        >
          {display}
        </button>
        <button
          type="button"
          onClick={startRename}
          aria-label={`Rename ${display}`}
          title="Rename"
          className="kd-focusable"
          style={{ display: "inline-flex", flex: "none", alignItems: "center", justifyContent: "center", padding: 3, background: "none", border: "none", borderRadius: "var(--radius-sm)", color: "var(--text-faint)", cursor: "pointer" }}
        >
          <Icon name="pencil" size={13} />
        </button>
      </span>
    );
  }

  const row = (indent: number): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    paddingLeft: 10 + indent * 18,
    borderBottom: "var(--border-w) solid var(--border)",
  });

  function rewordable(node: { origin?: string; key: string }) {
    return node.origin === "Custom" || (policy.allowEditWording && !locked(node.key));
  }

  /** A "Move to…" picker that re-parents a custom item to another action point (grouped by section). */
  function MoveControl({ itemKey, parentKey }: { itemKey: string; parentKey: string }) {
    const hasTarget = view.sections.some((sec) => sec.segments.some((sg) => sg.key !== parentKey));
    if (!hasTarget) return null;
    return (
      <select
        value=""
        disabled={busy}
        title={`Move to another ${noun.one}`}
        aria-label={`Move to another ${noun.one}`}
        onChange={(e) => { if (e.target.value) move(itemKey, e.target.value); }}
        className="kd-focusable"
        style={{ font: "inherit", fontSize: 12, fontWeight: 600, padding: "4px 8px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text-muted)", cursor: "pointer", maxWidth: 170 }}
      >
        <option value="" disabled>Move to…</option>
        {view.sections.map((sec) => {
          const opts = sec.segments.filter((sg) => sg.key !== parentKey);
          return opts.length ? (
            <optgroup key={sec.key} label={sec.label}>
              {opts.map((sg) => <option key={sg.key} value={sg.key}>{sg.name}</option>)}
            </optgroup>
          ) : null;
        })}
      </select>
    );
  }

  /** Delete a custom node, behind a two-click confirmation to avoid accidental loss. */
  function RemoveControl({ nodeKey, kind }: { nodeKey: string; kind: string }) {
    if (confirming === nodeKey) {
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text-muted)" }}>Delete {kind}?</span>
          <Act onClick={() => { setConfirming(null); remove(nodeKey); }} disabled={busy} danger><Icon name="trash" size={12} />Confirm</Act>
          <Act onClick={() => setConfirming(null)} disabled={busy}>Cancel</Act>
        </span>
      );
    }
    return <Act onClick={() => setConfirming(nodeKey)} disabled={busy} danger><Icon name="trash" size={12} />Delete</Act>;
  }

  /** Expanded item detail: an editable plain-text description and rich-text guidance. */
  function ItemDetail({ item, canEdit }: { item: ResolvedItem; canEdit: boolean }) {
    const desc = item.description ?? "";
    const editingDesc = editing?.key === item.key && editing.field === "description";
    const editingGuid = editing?.key === item.key && editing.field === "guidance";
    const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--text-faint)", marginBottom: 4 };
    const saveRow = (field: EditField) => (
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <Act onClick={() => saveField(item, "Item", field)} disabled={busy} title="Save"><Icon name="check" size={12} />Save</Act>
        <Act onClick={() => setEditing(null)} disabled={busy}>Cancel</Act>
      </div>
    );
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
        <div>
          <div style={labelStyle}>Description</div>
          {editingDesc ? (
            <>
              <textarea
                autoFocus
                rows={2}
                value={editing?.value ?? ""}
                disabled={busy}
                placeholder="Describe this item…"
                onChange={(e) => setEditing({ key: item.key, field: "description", value: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Escape") setEditing(null); }}
                style={{ font: "inherit", fontSize: 13, lineHeight: 1.5, width: "100%", padding: "6px 8px", border: "var(--border-w) solid var(--brand-400, #818cf8)", borderRadius: "var(--radius-sm)", background: "var(--surface)", color: "var(--text)", resize: "vertical", boxSizing: "border-box" }}
              />
              {saveRow("description")}
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <p style={{ margin: 0, flex: 1, fontSize: 13, lineHeight: 1.5, color: desc ? "var(--text-muted)" : "var(--text-faint)", fontStyle: desc ? "normal" : "italic" }}>{desc || "No description"}</p>
              {canEdit && <Act onClick={() => setEditing({ key: item.key, field: "description", value: desc })} disabled={busy}>{desc ? "Edit" : "Add"}</Act>}
            </div>
          )}
        </div>
        <div>
          <div style={labelStyle}>{guidanceLabel}</div>
          {editingGuid ? (
            <>
              <RichTextEditor value={editing?.value ?? ""} onChange={(html) => setEditing({ key: item.key, field: "guidance", value: html })} disabled={busy} placeholder="Add guidance for this item…" minHeight={56} />
              {saveRow("guidance")}
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <div style={{ flex: 1, fontSize: 13, lineHeight: 1.5, color: "var(--text-muted)" }}>
                {richTextHasContent(item.guidance) ? <RichText value={item.guidance} /> : <span style={{ color: "var(--text-faint)", fontStyle: "italic" }}>No guidance</span>}
              </div>
              {canEdit && <Act onClick={() => setEditing({ key: item.key, field: "guidance", value: item.guidance ?? "" })} disabled={busy}>{richTextHasContent(item.guidance) ? "Edit" : "Add"}</Act>}
            </div>
          )}
        </div>
      </div>
    );
  }

  function ItemRow({ item, parentKey }: { item: ResolvedItem; parentKey: string }) {
    const canEdit = rewordable(item);
    const hasDetail = canEdit || !!item.description || richTextHasContent(item.guidance); // expander only when there's something to show/edit
    const open = expanded.has(item.key);
    return (
      <>
        <div style={row(2)}>
          {hasDetail ? (
            <button
              type="button"
              onClick={() => toggleExpanded(item.key)}
              title={open ? "Hide details" : "Show details"}
              style={{ display: "inline-flex", flex: "none", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--text-faint)", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.12s" }}
            >
              <Icon name="chevDown" size={13} />
            </button>
          ) : (
            <span style={{ width: 6, height: 6, borderRadius: 99, background: "var(--text-faint)", flex: "none" }} />
          )}
          <NameCell node={item} nodeType="Item" field="name" display={item.name} canRename={canEdit} />
          {item.origin === "Custom" && <Tag label="Custom" tone="brand" />}
          {item.overridden && <Tag label="Edited" tone="amber" />}
          {item.origin === "Custom" ? (
            <>
              {policy.allowAddItems && <MoveControl itemKey={item.key} parentKey={parentKey} />}
              <RemoveControl nodeKey={item.key} kind="item" />
            </>
          ) : policy.allowDisableBaseNodes ? (
            <Act onClick={() => setDisabled(item.key, "Item", true)} disabled={busy}><Icon name="ban" size={12} />Hide</Act>
          ) : null}
        </div>
        {open && hasDetail && (
          <div style={{ ...row(3), borderBottom: "var(--border-w) solid var(--border)", background: "var(--surface-2)", paddingTop: 8, paddingBottom: 10 }}>
            <ItemDetail item={item} canEdit={canEdit} />
          </div>
        )}
      </>
    );
  }

  function SegmentRow({ segment, words = DEFAULT_ITEM_NOUN, listWords = noun }: { segment: ResolvedSegment; words?: ItemNoun; listWords?: SegmentNoun }) {
    return (
      <>
        <div style={row(1)}>
          <Icon name="chevDown" size={14} style={{ color: "var(--text-faint)", flex: "none" }} />
          {/* KEY-736: a day of a rolling-days section is generated — nothing to rename or hide; items may still be added. */}
          <NameCell node={segment} nodeType="Segment" field="name" display={segment.name} canRename={!segment.date && rewordable(segment)} />
          {segment.origin === "Custom" && <Tag label="Custom" tone="brand" />}
          {segment.overridden && <Tag label="Edited" tone="amber" />}
          {policy.allowAddItems && (
            <Act onClick={() => addItem(segment.key, words)} disabled={busy || !itemRoom(segment)} title={itemRoom(segment) ? `Add ${article(words.one)} ${words.one}` : `${words.One} limit reached`}><Icon name="plus" size={12} />{words.One}</Act>
          )}
          {segment.origin === "Custom" ? (
            <RemoveControl nodeKey={segment.key} kind={listWords.one} />
          ) : policy.allowDisableBaseNodes && !segment.date ? (
            <Act onClick={() => setDisabled(segment.key, "Segment", true)} disabled={busy}><Icon name="ban" size={12} />Hide</Act>
          ) : null}
        </div>
        {segment.items.map((item) => (
          <ItemRow key={item.key} item={item} parentKey={segment.key} />
        ))}
      </>
    );
  }

  return (
    <div className="kd-card" style={{ padding: "var(--pad-card)", display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "var(--text)" }}>Edit structure</h2>
        <p style={{ margin: "8px 0 0", color: "var(--text-muted)", fontSize: 14, lineHeight: 1.6 }}>
          Tailor this dial for your project: add sections, {noun.many} and items, rename them, edit their descriptions,
          or hide ones you don't need. Changes apply only to this dial.
        </p>
      </div>

      {/* Headroom */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", fontSize: 12.5, color: "var(--text-muted)", fontWeight: 600 }}>
        <span><Icon name="layers" size={13} style={{ verticalAlign: -2, marginRight: 4, color: "var(--text-faint)" }} />
          Sections: {view.sections.length}{policy.maxSections > 0 ? ` of ${policy.maxSections}` : ""} used
        </span>
        {headroom.maxSegmentsPerSection != null && <span>Max {headroom.maxSegmentsPerSection} {noun.many} / section</span>}
        {headroom.maxItemsPerSegment != null && <span>Max {headroom.maxItemsPerSegment} items / {noun.one}</span>}
      </div>

      {/* Items template: export the bespoke items to a reusable file, or import a file into this dial. */}
      {policy.allowAddItems && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <Act onClick={exportItems} disabled={busy || customItemCount === 0} title={customItemCount === 0 ? "No items to export yet" : "Download these items as a reusable file"}>
            <Icon name="download" size={12} />Export items{customItemCount > 0 ? ` (${customItemCount})` : ""}
          </Act>
          <Act onClick={() => fileInputRef.current?.click()} disabled={busy} title="Import items from a file">
            <Icon name="upload" size={12} />Import items
          </Act>
          <input ref={fileInputRef} type="file" accept=".json,application/json" onChange={onFilePicked} style={{ display: "none" }} />
          {docImportEnabled && (
            <Act onClick={() => { setErr(null); setNote(null); setDocImportOpen(true); }} disabled={busy} title={`Upload a report or checklist — AI proposes items mapped to your ${noun.many} for review`}>
              <Icon name="sparkles" size={12} />Import from document
            </Act>
          )}
        </div>
      )}

      {docImportOpen && (
        <DocumentItemImport
          noun={noun}
          instanceId={instanceId}
          view={view}
          itemAttributes={itemAttributes.filter((a) =>
            // Attributes limited to specific item keys can't apply to freshly minted items, so the
            // import only offers the unscoped ones and those limited to a whole action point (KEY-SCOPE);
            // the server enforces the same rule per item.
            // Only the extractable types are offered: the bulk-add write path drops a value for any other
            // type without saying so, so anything else here would be reviewed and accepted for nothing.
            a.target === "Item" && isAiExtractableAttributeType(a.type)
              && (!a.appliesToKeys?.length || a.appliesToKeys.some(isSegmentWideItemScope)))}
          statusDimension={statusDimension}
          onClose={() => setDocImportOpen(false)}
          onImported={async (r: BulkAddItemsReport) => {
            const bits = [`Added ${r.added} item${r.added === 1 ? "" : "s"} from the document`];
            if (r.statusesApplied > 0) bits.push(`set ${r.statusesApplied} status${r.statusesApplied === 1 ? "" : "es"}`);
            if (r.commentsPosted > 0) bits.push(`posted ${r.commentsPosted} inspector comment${r.commentsPosted === 1 ? "" : "s"}`);
            if (r.skippedCap > 0) bits.push(`skipped ${r.skippedCap} over the item limit`);
            if (r.skippedNoSegment > 0) bits.push(`skipped ${r.skippedNoSegment} with no matching ${noun.one}`);
            if (r.extrasSkipped > 0) bits.push(`${r.extrasSkipped} status/comment update${r.extrasSkipped === 1 ? "" : "s"} not permitted`);
            setNote(`${bits.join(", ")}.`);
            await onChanged();
          }}
        />
      )}

      {pendingImport && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--st-amber, #b45309)", background: "var(--surface-2)" }}>
          <Icon name="ban" size={14} style={{ color: "var(--st-amber, #b45309)", flex: "none" }} />
          <span style={{ flex: 1, minWidth: 200, fontSize: 13, color: "var(--text-muted)" }}>
            This dial already has {customItemCount} item{customItemCount === 1 ? "" : "s"}. Importing replaces the items in the {noun.many} this file covers and resets their statuses.
          </span>
          <Act onClick={() => doImport(pendingImport)} disabled={busy} danger><Icon name="upload" size={12} />Replace items</Act>
          <Act onClick={() => setPendingImport(null)} disabled={busy}>Cancel</Act>
        </div>
      )}

      {note && <p style={{ margin: 0, color: "var(--st-go, #16a34a)", fontSize: 13 }}>{note}</p>}

      {err && <p style={{ margin: 0, color: "var(--st-overdue, #dc2626)", fontSize: 13 }}>{err}</p>}

      {/* The resolved tree */}
      <div style={{ border: "var(--border-w) solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
        {view.sections.map((section) => (
          <div key={section.key}>
            <div style={{ ...row(0), background: "var(--surface-2)", fontWeight: 700 }}>
              <Icon name="layers" size={15} style={{ color: "var(--text-faint)", flex: "none" }} />
              <NameCell node={section} nodeType="Section" field="label" display={section.label} canRename={rewordable(section)} />
              {locked(section.key) && <Tag label="Locked" tone="muted" />}
              {section.origin === "Custom" && <Tag label="Custom" tone="brand" />}
              {section.overridden && <Tag label="Edited" tone="amber" />}
              {/* KEY-539: "Action-point limit reached" is REWORDED rather than templated — a supplied word
                  cannot be hyphenated safely ("key segment" → "Key-segment limit reached" is wrong). */}
              {policy.allowAddSegments && section.kind !== "Days" && (
                <Act onClick={() => addSegment(section.key, listNoun(section))} disabled={busy || !segmentRoom(section)} title={segmentRoom(section) ? `Add ${article(listNoun(section).one)} ${listNoun(section).one}` : `Limit reached — no more ${listNoun(section).many} here`}><Icon name="plus" size={12} />{listNoun(section).One}</Act>
              )}
              {section.origin === "Custom" ? (
                <RemoveControl nodeKey={section.key} kind="section" />
              ) : policy.allowDisableBaseNodes && !locked(section.key) ? (
                <Act onClick={() => setDisabled(section.key, "Section", true)} disabled={busy}><Icon name="ban" size={12} />Hide</Act>
              ) : null}
            </div>
            {section.segments.map((segment) => (
              <SegmentRow key={segment.key} segment={segment} words={itemNouns(section)} listWords={listNoun(section)} />
            ))}
          </div>
        ))}
        {view.sections.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "var(--text-faint)", fontSize: 14 }}>No sections yet.</div>}
      </div>

      {canAddSection && (
        <button type="button" onClick={addSection} disabled={busy} className="kd-focusable" style={{ font: "inherit", alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)", fontWeight: 600, fontSize: 13.5, cursor: "pointer" }}>
          <Icon name="plus" size={15} />Add section
        </button>
      )}

      {/* Hidden base nodes — re-enable */}
      {view.disabled.length > 0 && (
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)", marginBottom: 8 }}>Hidden nodes</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {view.disabled.map((d) => (
              <div key={`${d.nodeType}:${d.key}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface-2)" }}>
                <Icon name="ban" size={14} style={{ color: "var(--text-faint)" }} />
                <span style={{ flex: 1, fontSize: 13.5, color: "var(--text-muted)" }}>{d.nodeType} <code style={{ fontSize: 12 }}>{d.key}</code></span>
                <Act onClick={() => setDisabled(d.key, d.nodeType as NodeType, false)} disabled={busy}><Icon name="check" size={12} />Show</Act>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
